import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { L10N_LOCALE, L10nLocale, L10nTranslationModule } from 'angular-l10n';

/**
 * Shown when the identity provider is unreachable — reached when a sign-in or sign-out redirect
 * can't launch (see AuthService.handleAuthUnavailable). It sits outside the AuthGuard so rendering
 * it never re-triggers the sign-in redirect — that re-trigger is what causes the reload loop when
 * the IdP is down.
 *
 * To reach this screen without a real outage, simulate the IdP being down in Chrome DevTools:
 * open the Network tab → Network request blocking (⋮ / "More tools"), add the pattern
 * `*.well-known/openid-configuration` (or block the whole auth host, e.g. `auth.dev.alertsense.io`),
 * enable blocking, then reload. The OIDC discovery fetch fails and the app lands here instead of
 * looping. Uncheck the pattern and hit "Try again" to confirm normal login resumes.
 */
@Component({
  selector: 'app-auth-unavailable',
  templateUrl: './auth-unavailable.component.html',
  styleUrls: ['./auth-unavailable.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatButtonModule, MatIconModule, L10nTranslationModule],
  standalone: true,
})
export class AuthUnavailableComponent {
  protected readonly locale = inject<L10nLocale>(L10N_LOCALE);

  /**
   * "Try again" does a full reload of the app root so the whole auth bootstrap runs fresh; if the
   * IdP has recovered the normal login flow proceeds, otherwise we land back here.
   */
  retry(): void {
    globalThis.location.href = globalThis.location.origin;
  }
}
